import { Controller } from "@hotwired/stimulus"
import { followBottom } from "../utils/chat_scroll"
import { createConsumer } from "@rails/actioncable"

// Webcam — a 1:1 live video call over WebRTC, signaled through WebcamChannel.
//
// Roles: user_a is the CALLER (config.caller === "true"). Both peers publish
// their camera + mic; a lightweight presence handshake (peer_joined / peer_here
// over the cable) tells the caller the other side is here, and the caller then
// creates the SDP offer. The callee answers. ICE candidates and SDP flow over
// the cable's `signal` action; each client ignores its own echoes (from ===
// userId).
//
// TURN: iceServers come from the server (STUN always, TURN when TURN_URL etc.
// are set) — TURN is the prod requirement for NAT traversal.
//
// Graceful degradation: no camera / denied permission / unsupported browser →
// a clear message on the stage overlay (never a blank frame), and chat still
// works over the cable. Cable trouble → a status line. Every media/cable/peer
// call is wrapped defensively.
export default class extends Controller {
    static targets = ["remoteVideo", "localVideo", "overlay", "overlayText", "overlayActions", "micBtn", "camBtn", "messages", "chatInput"]

    connect() {
        this.config = this.#readConfig()
        this.userId = parseInt(this.config.userId || "0", 10)
        this.isCaller = this.config.caller === "true"
        this.token = this.config.token
        this.iceServers = this.#parseIceServers(this.config.iceServers)
        this.peerPresent = false
        this.offered = false
        this.connected = false
        this.remoteReady = false      // setRemoteDescription has landed
        this.pendingCandidates = []   // ICE that arrived before the remote SDP
        this.micOn = true
        this.camOn = true

        if (this.config.status === "ended") {
            this.#showOverlay("This call has ended.", { spinner: false, actions: true })
            return
        }

        this.#init()
    }

    disconnect() {
        clearTimeout(this.waitTimer)
        clearTimeout(this.iceGraceTimer)
        this.#stopLocal()
        if (this.pc) { try { this.pc.close() } catch (e) { /* noop */ } }
        if (this.subscription) { try { this.subscription.unsubscribe() } catch (e) { /* noop */ } }
        if (this.consumer) { try { this.consumer.disconnect() } catch (e) { /* noop */ } }
    }

    async #init() {
        await this.#startMedia()
        this.#createPeer()
        this.#connectCable()
        // If nobody shows up in a while, offer a graceful way out.
        this.waitTimer = setTimeout(() => {
            if (!this.connected) this.#showOverlay(`Still waiting for ${this.config.peerName}…`, { spinner: true, actions: true })
        }, 30000)
    }

    #readConfig() {
        const el = this.element.querySelector("#webcam-config")
        return el ? { ...el.dataset } : {}
    }

    #parseIceServers(raw) {
        try {
            const parsed = JSON.parse(raw || "[]")
            return Array.isArray(parsed) && parsed.length ? parsed : [{ urls: "stun:stun.l.google.com:19302" }]
        } catch (e) {
            return [{ urls: "stun:stun.l.google.com:19302" }]
        }
    }

    // --- Media --------------------------------------------------------------

    async #startMedia() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.mediaFailed = true
            this.#showOverlay("This browser can't access your camera here. Chat still works.", { spinner: false })
            return
        }
        try {
            this.localStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
            if (this.hasLocalVideoTarget) this.localVideoTarget.srcObject = this.localStream
        } catch (e) {
            this.mediaFailed = true
            const denied = e && (e.name === "NotAllowedError" || e.name === "SecurityError")
            const msg = denied
                ? "Camera and mic are blocked. Allow access in your browser, then reload."
                : "We couldn't reach your camera or mic. You can still chat below."
            this.#showOverlay(msg, { spinner: false, actions: true })
        }
    }

    #stopLocal() {
        if (this.localStream) { try { this.localStream.getTracks().forEach((t) => t.stop()) } catch (e) { /* noop */ } }
    }

    // --- Peer connection ----------------------------------------------------

    #createPeer() {
        if (typeof RTCPeerConnection !== "function") {
            this.#showOverlay("This browser doesn't support video calls. Try a recent Chrome, Safari or Firefox.", { spinner: false })
            return
        }
        try {
            this.pc = new RTCPeerConnection({ iceServers: this.iceServers })
        } catch (e) {
            this.#showOverlay("Couldn't start the video engine.", { spinner: false, actions: true })
            return
        }
        // A fresh peer starts with no remote SDP and an empty candidate buffer.
        this.remoteReady = false
        this.pendingCandidates = []
        this.#resetDiag()
        if (this.localStream) {
            this.localStream.getTracks().forEach((t) => { try { this.pc.addTrack(t, this.localStream) } catch (e) { /* noop */ } })
        }
        this.pc.ontrack = (e) => this.#onRemoteTrack(e)
        this.pc.onicecandidate = (e) => this.#onLocalCandidate(e)
        this.pc.onicegatheringstatechange = () => this.#debugDiag(`gathering ${this.pc.iceGatheringState}`)
        this.pc.oniceconnectionstatechange = () => this.#onIceStateChange()
        this.pc.onconnectionstatechange = () => this.#onConnectionStateChange()
    }

    // Tear down the current peer and build a clean one — used when the other
    // side leaves/reloads so a re-join renegotiates from scratch instead of
    // inheriting a half-dead RTCPeerConnection (stale m-lines / ICE state).
    #resetPeer() {
        if (this.pc) { try { this.pc.close() } catch (e) { /* noop */ } }
        this.pc = null
        this.offered = false
        this.remoteReady = false
        this.pendingCandidates = []
        this.#createPeer()
    }

    #onRemoteTrack(event) {
        const stream = event.streams && event.streams[0]
        if (stream && this.hasRemoteVideoTarget) {
            this.remoteVideoTarget.srcObject = stream
            // Autoplay of a stream carrying audio can be blocked; nudge it and
            // swallow the rejection (the user already gestured to start the call).
            const p = this.remoteVideoTarget.play()
            if (p && typeof p.catch === "function") p.catch(() => { /* autoplay policy — harmless */ })
            this.connected = true
            this.#hideOverlay()
        }
    }

    // ICE-level transitions. `disconnected` is TRANSIENT per the WebRTC spec —
    // ICE routinely blips there and recovers on its own — so we give it a grace
    // window before surfacing trouble, and only treat `failed` as needing an
    // ICE restart. Reacting to `disconnected` instantly (the old behavior) tore
    // the UI into "Connection lost" on every momentary blip.
    #onIceStateChange() {
        if (!this.pc) return
        const state = this.pc.iceConnectionState
        this.#debug(`ice ${state}`)
        this.#recordState(state)
        clearTimeout(this.iceGraceTimer)
        if (state === "connected" || state === "completed") {
            this.connected = true
            this.#hideOverlay()
            this.#postDiag("connected")
        } else if (state === "disconnected") {
            // Wait ~6s; if it hasn't self-healed, show the status and try a restart.
            this.iceGraceTimer = setTimeout(() => {
                if (!this.pc) return
                const s = this.pc.iceConnectionState
                if (s === "disconnected" || s === "failed") {
                    // Grace expired without recovery — treat as a terminal failure
                    // for diagnostics, then still attempt a restart.
                    this.#postDiag("failed")
                    this.#showOverlay("Connection lost - trying to reconnect…", { spinner: true, actions: false })
                    this.#restartIce()
                    this.#maybeRelayHint() // last word: overrides the reconnect text when no relay
                }
            }, 6000)
        } else if (state === "failed") {
            this.#postDiag("failed")
            this.#showOverlay("Connection lost - trying to reconnect…", { spinner: true, actions: false })
            this.#restartIce()
            this.#maybeRelayHint() // last word: overrides the reconnect text when no relay
        }
    }

    #onConnectionStateChange() {
        if (!this.pc) return
        const state = this.pc.connectionState
        this.#debugDiag(`pc ${state} @${this.#elapsed()}ms`)
        if (state === "connected") {
            this.#postDiag("connected")
        } else if (state === "failed") {
            this.#postDiag("failed")
            this.#maybeRelayHint()
        }
    }

    // Only the caller drives (re)negotiation, so an ICE restart is a fresh
    // offer with { iceRestart: true }. The callee just answers it. TURN (when
    // provisioned server-side) gives the restart a relay path to succeed on.
    #restartIce() {
        if (!this.pc) return
        if (this.isCaller) {
            if (typeof this.pc.restartIce === "function") { try { this.pc.restartIce() } catch (e) { /* noop */ } }
            this.offered = false
            this.#makeOffer({ iceRestart: true })
        }
    }

    async #makeOffer(options = {}) {
        if (!this.pc || !this.isCaller || this.offered) return
        this.offered = true
        try {
            const offer = await this.pc.createOffer(options)
            await this.pc.setLocalDescription(offer)
            this.#send("signal", { kind: "offer", payload: this.pc.localDescription })
        } catch (e) {
            this.offered = false
            this.#showOverlay("Couldn't start the video connection.", { spinner: false, actions: true })
        }
    }

    async #applySignal(data) {
        if (!this.pc) return
        try {
            if (data.kind === "offer") {
                await this.pc.setRemoteDescription(data.payload)
                await this.#flushCandidates()
                const answer = await this.pc.createAnswer()
                await this.pc.setLocalDescription(answer)
                this.#send("signal", { kind: "answer", payload: this.pc.localDescription })
            } else if (data.kind === "answer") {
                await this.pc.setRemoteDescription(data.payload)
                await this.#flushCandidates()
            } else if (data.kind === "ice") {
                // An ICE candidate that arrives before the remote SDP would throw
                // ("remote description was null") and be lost — starving ICE and
                // producing "connects for a second, then drops". Queue until the
                // remote description lands, then add.
                if (this.remoteReady) {
                    this.#recordRemoteCandidate(data.payload)
                    await this.pc.addIceCandidate(data.payload)
                } else {
                    this.pendingCandidates.push(data.payload)
                }
            }
        } catch (e) { this.#debug(`signal ${data && data.kind} error: ${e && e.name}`) }
    }

    async #flushCandidates() {
        this.remoteReady = true
        const queued = this.pendingCandidates
        this.pendingCandidates = []
        for (const c of queued) {
            try { this.#recordRemoteCandidate(c); await this.pc.addIceCandidate(c) } catch (e) { this.#debug(`late ice error: ${e && e.name}`) }
        }
    }

    #debug(msg) {
        if (typeof console !== "undefined" && console.debug) console.debug(`[webcam] ${msg}`)
    }

    // --- ICE diagnostics ----------------------------------------------------
    //
    // Per-call, PII-free connectivity telemetry: how many local/remote ICE
    // candidates of each type we gathered, the timeline of ICE-connection-state
    // transitions (with elapsed ms), and — once connected — the type of the
    // candidate pair actually selected. Console-logged as it happens under the
    // `[webcam:diag]` prefix, and POSTed to the server once on the first
    // terminal outcome per outcome kind (connected / failed) so failures are
    // diagnosable from the logs. Types and states only — never IPs/addresses.

    #resetDiag() {
        this.diag = {
            t0: (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now(),
            local: { host: 0, srflx: 0, relay: 0, prflx: 0 },
            remote: { host: 0, srflx: 0, relay: 0, prflx: 0 },
            states: [],
            posted: {}
        }
    }

    #elapsed() {
        if (!this.diag) return 0
        const now = (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now()
        return Math.round(now - this.diag.t0)
    }

    #debugDiag(msg) {
        if (typeof console !== "undefined" && console.debug) console.debug(`[webcam:diag] ${msg}`)
    }

    // Parse the candidate type ("host"/"srflx"/"relay"/"prflx") out of the SDP
    // candidate line without touching the address — `... typ srflx ...`.
    #candidateType(candidate) {
        const line = candidate && (typeof candidate === "string" ? candidate : candidate.candidate)
        const m = /(?:^| )typ (host|srflx|relay|prflx)\b/.exec(line || "")
        return m ? m[1] : null
    }

    #onLocalCandidate(event) {
        if (!event) return
        if (event.candidate) {
            const type = this.#candidateType(event.candidate)
            if (type && this.diag) {
                this.diag.local[type] = (this.diag.local[type] || 0) + 1
                this.#debugDiag(`local ${type} (host=${this.diag.local.host} srflx=${this.diag.local.srflx} relay=${this.diag.local.relay} prflx=${this.diag.local.prflx})`)
            }
            this.#send("signal", { kind: "ice", payload: event.candidate })
        } else if (this.diag) {
            // null candidate → gathering complete; log the final tally.
            this.#debugDiag(`local gathered host=${this.diag.local.host} srflx=${this.diag.local.srflx} relay=${this.diag.local.relay} prflx=${this.diag.local.prflx}`)
        }
    }

    #recordRemoteCandidate(candidate) {
        const type = this.#candidateType(candidate)
        if (type && this.diag) {
            this.diag.remote[type] = (this.diag.remote[type] || 0) + 1
            this.#debugDiag(`remote ${type} (host=${this.diag.remote.host} srflx=${this.diag.remote.srflx} relay=${this.diag.remote.relay} prflx=${this.diag.remote.prflx})`)
        }
    }

    #recordState(state) {
        if (!this.diag) return
        const t = this.#elapsed()
        this.diag.states.push({ s: state, t })
        this.#debugDiag(`ice ${state} @${t}ms`)
    }

    // Inspect getStats() for the succeeded/nominated candidate pair and report
    // its local/remote candidate TYPES (e.g. "host/host", "relay/srflx"). Never
    // reads addresses. Returns "none" when there's no selected pair yet.
    async #selectedPairTypes() {
        if (!this.pc || typeof this.pc.getStats !== "function") return "none"
        try {
            const stats = await this.pc.getStats()
            const local = {}
            const remote = {}
            let pair = null
            stats.forEach((r) => {
                if (r.type === "local-candidate") local[r.id] = r.candidateType
                else if (r.type === "remote-candidate") remote[r.id] = r.candidateType
            })
            stats.forEach((r) => {
                if (r.type !== "candidate-pair") return
                const chosen = r.state === "succeeded" && (r.nominated || r.selected)
                if (chosen && !pair) pair = r
            })
            if (!pair) stats.forEach((r) => { if (!pair && r.type === "candidate-pair" && r.state === "succeeded") pair = r })
            if (!pair) return "none"
            const l = local[pair.localCandidateId] || "?"
            const rr = remote[pair.remoteCandidateId] || "?"
            return `${l}/${rr}`
        } catch (e) {
            return "none"
        }
    }

    // Show a subtle, terminal-only hint when we gathered zero relay candidates
    // and the connection failed — the honest diagnosis is "no relay route".
    #maybeRelayHint() {
        if (!this.diag || this.diag.local.relay > 0) return
        this.#showOverlay("Couldn't find a route between you — this network needs a relay server.", { spinner: false, actions: true })
    }

    async #postDiag(outcome) {
        if (!this.diag || this.diag.posted[outcome]) return
        this.diag.posted[outcome] = true
        const pair = outcome === "connected" ? await this.#selectedPairTypes() : "none"
        const states = this.diag.states.map((x) => `${x.s}@${x.t}`)
        this.#debugDiag(`post ${outcome} local=${JSON.stringify(this.diag.local)} remote=${JSON.stringify(this.diag.remote)} states=[${states.join(",")}] pair=${pair}`)
        const body = {
            token: this.token,
            outcome,
            local: this.diag.local,
            remote: this.diag.remote,
            states,
            pair
        }
        try {
            const meta = document.querySelector('meta[name="csrf-token"]')
            await fetch("/apps/webcam/diagnostics", {
                method: "POST",
                keepalive: true,
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": meta ? meta.content : "",
                    "Accept": "application/json"
                },
                body: JSON.stringify(body)
            })
        } catch (e) { /* diagnostics are best-effort; never surface to the user */ }
    }

    // --- Cable --------------------------------------------------------------

    #connectCable() {
        if (typeof createConsumer !== "function") { this.#showOverlay("Connection lost - reload to try again.", { spinner: false, actions: true }); return }
        try {
            this.consumer = createConsumer()
            this.subscription = this.consumer.subscriptions.create(
                { channel: "WebcamChannel", token: this.token },
                {
                    connected: () => this.#onCableConnected(),
                    disconnected: () => { if (!this.connected) this.#setOverlayText("Reconnecting…") },
                    rejected: () => this.#showOverlay("You can't join this call.", { spinner: false, actions: true }),
                    received: (data) => this.#onReceived(data)
                }
            )
        } catch (e) {
            this.#showOverlay("Connection lost - reload to try again.", { spinner: false, actions: true })
        }
    }

    #onCableConnected() {
        // Announce myself so the peer (whichever order we arrived) learns I'm
        // present; the caller acts on that to send its offer.
        this.#send("ready", {})
    }

    #onReceived(data) {
        if (!data || !data.type) return
        if (data.from && data.from === this.userId && data.type !== "chat") return // ignore my own echoes

        switch (data.type) {
            case "peer_joined":
                if (data.from !== this.userId) { this.#send("ready", {}); this.#peerHere() }
                break
            case "peer_here":
                if (data.from !== this.userId) this.#peerHere()
                break
            case "signal":
                this.#applySignal(data)
                break
            case "chat":
                this.#appendChat(data.html)
                break
            case "peer_left":
                if (data.from !== this.userId) this.#onPeerLeft()
                break
            case "ended":
                this.#onEnded()
                break
        }
    }

    #peerHere() {
        this.peerPresent = true
        if (!this.connected) this.#setOverlayText(`Connecting to ${this.config.peerName}…`)
        this.#makeOffer()
    }

    #onPeerLeft() {
        this.connected = false
        this.peerPresent = false
        clearTimeout(this.iceGraceTimer)
        if (this.hasRemoteVideoTarget) this.remoteVideoTarget.srcObject = null
        // Rebuild a clean peer so if they reload/rejoin we renegotiate from
        // scratch rather than on a stale connection.
        this.#resetPeer()
        this.#showOverlay(`${this.config.peerName} left the call.`, { spinner: false, actions: true })
    }

    #onEnded() {
        this.connected = false
        if (this.hasRemoteVideoTarget) this.remoteVideoTarget.srcObject = null
        this.#showOverlay("The call has ended.", { spinner: false, actions: true })
    }

    #send(action, payload) {
        if (!this.subscription) return
        try { this.subscription.perform(action, payload) } catch (e) { /* noop */ }
    }

    // --- Controls -----------------------------------------------------------

    toggleMic() {
        if (!this.localStream) return
        this.micOn = !this.micOn
        this.localStream.getAudioTracks().forEach((t) => { t.enabled = this.micOn })
        if (this.hasMicBtnTarget) {
            this.micBtnTarget.classList.toggle("is-off", !this.micOn)
            this.micBtnTarget.setAttribute("aria-label", this.micOn ? "Mute microphone" : "Unmute microphone")
            const icon = this.micBtnTarget.querySelector("i")
            if (icon) icon.className = this.micOn ? "bi bi-mic-fill" : "bi bi-mic-mute-fill"
        }
    }

    toggleCam() {
        if (!this.localStream) return
        this.camOn = !this.camOn
        this.localStream.getVideoTracks().forEach((t) => { t.enabled = this.camOn })
        if (this.hasLocalVideoTarget) this.localVideoTarget.classList.toggle("is-off", !this.camOn)
        if (this.hasCamBtnTarget) {
            this.camBtnTarget.classList.toggle("is-off", !this.camOn)
            this.camBtnTarget.setAttribute("aria-label", this.camOn ? "Turn off camera" : "Turn on camera")
            const icon = this.camBtnTarget.querySelector("i")
            if (icon) icon.className = this.camOn ? "bi bi-camera-video-fill" : "bi bi-camera-video-off-fill"
        }
    }

    // Overlay action buttons (they-left / ended / timeout).

    backToLobby() { window.location.href = "/apps/webcam" }

    // --- Chat ---------------------------------------------------------------

    chat(event) {
        event.preventDefault()
        if (!this.hasChatInputTarget) return
        const body = this.chatInputTarget.value.trim()
        if (!body) return
        if (!this.subscription) { this.#setOverlayText("Chat is offline right now."); return }
        this.#send("chat", { body })
        this.chatInputTarget.value = ""
    }

    #appendChat(html) {
        if (!this.hasMessagesTarget || !html) return
        this.messagesTarget.insertAdjacentHTML("beforeend", html)
        followBottom(this, this.messagesTarget).contentChanged()
    }

    // --- Overlay ------------------------------------------------------------

    #showOverlay(text, { spinner = true, actions = false } = {}) {
        if (!this.hasOverlayTarget) return
        this.overlayTarget.hidden = false
        this.#setOverlayText(text)
        const ring = this.overlayTarget.querySelector(".webcam-spinner")
        if (ring) ring.hidden = !spinner
        if (this.hasOverlayActionsTarget) {
            if (actions) {
                this.overlayActionsTarget.hidden = false
                this.overlayActionsTarget.innerHTML = this.#actionsHtml()
            } else {
                this.overlayActionsTarget.hidden = true
                this.overlayActionsTarget.innerHTML = ""
            }
        }
    }

    #actionsHtml() {
        return '<button type="button" class="webcam-overlay-btn is-ghost" data-action="webcam#backToLobby">Back to lobby</button>'
    }

    #setOverlayText(text) {
        if (this.hasOverlayTextTarget) this.overlayTextTarget.textContent = text || ""
    }

    #hideOverlay() {
        clearTimeout(this.waitTimer)
        if (this.hasOverlayTarget) this.overlayTarget.hidden = true
    }
}
